import { Collapse } from 'reactstrap';
import { useState, useEffect } from 'react';
import { CloseIcon, EditIcon } from '@iconicicons/react';
import {
  useGetCapacityComponentFlowHistoryQuery,
  useCreateUpdateCapacityComponentMutation,
} from '../services/edgeApi';
import { usePoints } from '../points/usePoints';
import { useConfirm } from '../helpers/hooks';
import { Can } from '../user/Can';
import { LoadingButton } from '../helpers/LoadingButton';
import { FloatingLabelInput } from '../helpers/FloatingLabelInput';
import { DjangoPointSearch } from '../search/DjangoPointSearch';
import { PointListItem } from '../points/PointListItem';

export function ComponentLocationManager({ componentId, setComponent }) {
  const points = usePoints();
  const [
    updateComponent,
    { isLoading: isCreatingUpdating, isSuccess, reset: resetMutation },
  ] = useCreateUpdateCapacityComponentMutation();

  // get component - could pass results that we already have, or grab from backend
  const { data: component, isLoading } =
    useGetCapacityComponentFlowHistoryQuery({ componentId });
  const confirm = useConfirm();
  const [metaOpen, setMetaOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // component isn't ready on initial load, set in useEffect once component is ready
  const [alteredComponent, setAlteredComponent] = useState(false);

  useEffect(() => {
    setAlteredComponent(component);
  }, [component]);

  useEffect(() => {
    setIsEditing(false);
    resetMutation();
  }, [isSuccess]);

  useEffect(() => {
    if (component && setComponent) {
      setComponent(component);
    }
  }, [component]);

  const addPoint = (id) => {
    // add point ID to point field, update capacity component
    const capacityComponent = {
      ...component,
      points: [...component.points, id],
    };
    updateComponent(capacityComponent);
  };

  const removePoint = (removePointId) => {
    if (!confirm('Are you sure?')) {
      return null;
    }
    const capacityComponent = {
      ...component,
      points: component.points.filter((point) => point != removePointId),
    };
    updateComponent(capacityComponent);
  };

  const handleChange = (evt) => {
    const { name } = evt.target;
    const value =
      evt.target.type === 'checkbox' ? evt.target.checked : evt.target.value;
    setAlteredComponent({ ...alteredComponent, [name]: value });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (!component) {
    return null;
  }

  return (
    <div>
      {/* TODO: put this in header of modal */}
      <form className="mb-3" onSubmit={() => updateComponent(alteredComponent)}>
        {isEditing ? (
          <>
            <FloatingLabelInput
              label="Identifier"
              onChange={handleChange}
              name="identifier"
              value={alteredComponent.identifier ?? ''}
            />
            <FloatingLabelInput
              label="Description"
              onChange={handleChange}
              name="description"
              value={alteredComponent.description}
            />
          </>
        ) : (
          <div>
            {component.label}
            <EditIcon
              className="float-end text-warning"
              onClick={() => setIsEditing(!isEditing)}
            />
          </div>
        )}
        {isEditing ? (
          <FloatingLabelInput
            label="Flow Direction"
            type="select"
            onChange={handleChange}
            name="flow_direction"
            value={alteredComponent.flow_direction}
          >
            <option value="FORWARDHAUL">FORWARDHAUL</option>
            <option value="BACKHAUL">BACKHAUL</option>
          </FloatingLabelInput>
        ) : (
          <div>{component.flow_direction}</div>
        )}
        {isEditing ? (
          <FloatingLabelInput
            label="Type"
            type="select"
            onChange={handleChange}
            name="capacity_type"
            value={alteredComponent.capacity_type}
          >
            <option value="POINT">POINT</option>
            <option value="SEGMENT">SEGMENT</option>
          </FloatingLabelInput>
        ) : (
          <div>{component.capacity_type}</div>
        )}
        {isEditing && (
          <>
            <LoadingButton
              className="btn btn-primary w-50"
              onClick={() => updateComponent(alteredComponent)}
              isLoading={isCreatingUpdating}
            >
              Save
            </LoadingButton>
            <button
              className="btn btn-outline-warning w-50"
              onClick={() => {
                setAlteredComponent(component);
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
          </>
        )}
      </form>
      {/* <EditableField name="flow_direction" value={component.flow_direction} isEditing={isEditing} onChange={}/> */}
      {component.platts_metadata && (
        <div className="card mb-3">
          <div className="card-header" onClick={() => setMetaOpen(!metaOpen)}>
            Platts Metadata -{component.platts_metadata.point_name}
          </div>
          <Collapse isOpen={metaOpen}>
            <div className="card-body">
              {Object.entries(component.platts_metadata).map(([key, value]) => (
                <div key={key}>
                  <span>{key}:</span> <span className="float-end">{value}</span>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      )}
      <h6>Points</h6>
      <ul className="list-group">
        {component.points.map((pointId) => {
          const point = points[pointId];
          if (!point) {
            return null;
          }
          return (
            <PointListItem point={point} key={point.id.toString()}>
              <Can can="delete_capacitycomponentmatching">
                <button
                  className="float-end btn btn-sm btn-primary-outline text-danger"
                  onClick={() => removePoint(pointId)}
                >
                  <CloseIcon />
                </button>
              </Can>
            </PointListItem>
          );
        })}
      </ul>
      <Can can="add_capacitycomponentmatching">
        {/* need to calculate results from query and additional filters, which we can pass in */}
        {/* need to add an onclick handler - does it have to be passed in as prop or can it be composed somehow? */}
        <DjangoPointSearch
          onSelect={addPoint}
          filters={{ tsp: component.tsp }}
        />
      </Can>
    </div>
  );
}

{
  /* <Results>
  <PointItem />
</Results> */
}
